import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '@env/insitestore360';
import {
  ensureSessionProjectTokens,
  isInteluxProject,
  isPmProject,
  isStore360ProjectRole,
  resolvePostLoginAppPath,
  normalizeAppPath,
  resolveProjectAppPath,
  type ProjectTokenEntry,
} from '@insite-store360/auth-session';
import { CommonService, LoginService, StorageService, UserServiceService } from '@insite-store360/services';
import { resolveProjectManagementRoleId } from '@insite-store360/UI';
import { SALES, SALES_DIRECTOR_GM, SALES_MANAGER } from 'library/api-common/src/lib/utils/appliationConstants';
import { ToastrService } from 'ngx-toastr';

const POST_LOGIN_ROUTE = '/admin/landing-page';
const PM_POST_LOGIN_ROUTE = '/admin/project-management/leads-summary';
const LAST_VISITED_ROUTE_KEY = 'lastVisitedRoute';

function isProjectId2SalesUser(roles: Array<{ id?: number; projectId?: number }>): boolean {
  const salesRoleIds = [SALES, SALES_MANAGER, SALES_DIRECTOR_GM];
  return roles.some(
    (role) =>
      Number(role.projectId) === 2 && salesRoleIds.includes(Number(role.id)),
  );
}

@Component({
  selector: 'lib-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  public disableButton: boolean = false;
  public timer: number = 60; // Initial timer value in seconds
  private countdownTimer: any;

  public isEnableOtp: boolean = false;
  loginForm: FormGroup;
  public isPasswordField: boolean = false;

  // Custom validator for Electrolux email
  private electroluxEmailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const email = control.value;
    if (!email.endsWith('@electrolux.com')) {
      return { electroluxEmail: true };
    }

    return null;
  }

  // Getter to check if login button should be disabled
  get isLoginDisabled(): boolean {
    const emailControl = this.loginForm.get('email');
    return !emailControl?.valid || emailControl?.hasError('electroluxEmail');
  }

  constructor(
    private fb: FormBuilder,
    private loginService: LoginService,
    private toastr: ToastrService,
    private router: Router,
    private storage: StorageService,
    private commonService: CommonService,
    private userService: UserServiceService,
    // private notificationService: TimelineWebSocketService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, this.electroluxEmailValidator.bind(this)]],
      otp: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern('^[0-9]{6}$')  // This ensures exactly 6 digits
      ]],
      // password: ['', [Validators.required]],
    });

  }

  private resolvePostLoginRoute(
    user: {
      roles?: Array<{ id?: number; projectId?: number }>;
      hasProjectManagementAccess?: boolean;
    },
    projects: ProjectTokenEntry[] = [],
  ): string {
    const userRoles = user?.roles ?? [];
    const projectRoles = projects.flatMap(
      (p) => (p.roles as Array<{ id?: number; projectId?: number }>) ?? [],
    );
    const roles = [...userRoles, ...projectRoles];
    const hasPmAccess = user?.hasProjectManagementAccess === true;
    const hasOnlyNonInteluxRoles =
      roles.length > 0 &&
      roles.every(
        (role) => role.projectId != null && Number(role.projectId) > 1,
      );
    const hasProjectId1 =
      !hasOnlyNonInteluxRoles &&
      (projects.some(isInteluxProject) ||
        roles.some(
          (role) =>
            role.projectId != null &&
            (Number(role.projectId) === 1 || Number(role.projectId) === 0),
        ) ||
        roles.some(isStore360ProjectRole));
    const hasProjectId2 = roles.some((role) => Number(role.projectId) === 2);
    let storedRedirect = localStorage.getItem('redirectUrl')?.trim();
    if (
      !storedRedirect ||
      storedRedirect === '/' ||
      storedRedirect.endsWith('/login') ||
      storedRedirect.includes('/login?')
    ) {
      storedRedirect = undefined;
    }

    // IntElux access:
    // - Store360 roles (projectId 1 / legacy null), or
    // - toggle PM users in project_management_user without native project-2 roles
    //   (those users historically have IntElux + PM), or
    // - IntElux already present in project-tokens.
    // Do not treat project-tokens-p1 alone as IntElux (saved for every login).
    const isTogglePmDualAccess = hasPmAccess && !hasProjectId2;
    const hasInteluxAccess =
      !hasOnlyNonInteluxRoles &&
      (hasProjectId1 ||
        isTogglePmDualAccess ||
        projects.some(isInteluxProject));
    const hasOtherProjectAccess =
      hasProjectId2 ||
      hasPmAccess ||
      projects.some((project) => !isInteluxProject(project));
    const isMultiProjectUser =
      projects.length > 1 || (hasInteluxAccess && hasOtherProjectAccess);

    // Multi-project / toggle-PM dual access: open IntElux first.
    // Ignore stale PM redirects / last-visited PM pages from a prior session.
    if (isMultiProjectUser) {
      this.activateDefaultInteluxSession(projects);
      const inteluxRedirect =
        storedRedirect && !storedRedirect.includes('/project-management/')
          ? storedRedirect
          : undefined;
      localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
      return (
        inteluxRedirect ||
        resolvePostLoginAppPath(projects) ||
        POST_LOGIN_ROUTE
      );
    }

    // Single project token → that project's JWT + default route
    if (projects.length === 1) {
      const only = projects[0];
      const isStandaloneProject =
        !isInteluxProject(only) && !isPmProject(only);
      const projectPath = resolveProjectAppPath(only);

      localStorage.setItem(
        'isProjectManager',
        isPmProject(only) ? 'true' : 'false',
      );
      if (only.projectId != null) {
        localStorage.setItem('active-project-id', String(only.projectId));
      }

      // SPFT / Shop Profiling: keep the host global JWT in auth-token.
      // The project-scoped JWT stays in project-tokens for the MFE. Swapping
      // auth-token to the SPFT JWT makes user-service 401 and reloads the app.
      if (isStandaloneProject) {
        const globalToken = localStorage.getItem('project-tokens-p1');
        if (globalToken) {
          this.storage.saveToken(globalToken);
          localStorage.setItem('auth-token', globalToken);
          localStorage.setItem('accessToken', globalToken);
        }
        localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
        localStorage.removeItem('redirectUrl');
        return projectPath;
      }

      if (only.token) {
        this.storage.saveToken(only.token);
        localStorage.setItem('auth-token', only.token);
        localStorage.setItem('accessToken', only.token);
      }

      return (
        storedRedirect ||
        projectPath ||
        (isPmProject(only) ? PM_POST_LOGIN_ROUTE : POST_LOGIN_ROUTE)
      );
    }

    // Native PM-only (projectId 2 / sales roles, no IntElux). Do NOT treat
    // project_management_user alone as PM-only — that flag means dual access.
    const isPmOnly =
      !hasProjectId1 &&
      !hasPmAccess &&
      (isProjectId2SalesUser(roles) || hasProjectId2);
    if (isPmOnly) {
      localStorage.setItem('isProjectManager', 'true');
      return resolvePostLoginAppPath(projects, {
        preferPmRoute: true,
        pmRoute: PM_POST_LOGIN_ROUTE,
        redirectUrl: storedRedirect,
      });
    }

    localStorage.setItem('isProjectManager', 'false');
    return resolvePostLoginAppPath(projects, {
      redirectUrl: storedRedirect,
    });
  }

  /** Keep global IntElux token active and mark project 1 as selected. */
  private activateDefaultInteluxSession(projects: ProjectTokenEntry[]): void {
    localStorage.setItem('isProjectManager', 'false');
    const globalToken = localStorage.getItem('project-tokens-p1');
    if (globalToken) {
      this.storage.saveToken(globalToken);
      localStorage.setItem('auth-token', globalToken);
      localStorage.setItem('accessToken', globalToken);
    }

    const intelux = projects.find(isInteluxProject);
    if (intelux?.projectId != null) {
      localStorage.setItem('active-project-id', String(intelux.projectId));
    } else {
      // Synthetic IntElux default (projectId null in switcher)
      localStorage.removeItem('active-project-id');
    }
    // Prevent shell from reopening a saved PM page under IntElux.
    const saved = localStorage.getItem(LAST_VISITED_ROUTE_KEY) || '';
    if (saved.includes('/project-management/')) {
      localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
    }
  }

  /** Stores this browser's FCM token. Login still continues if notifications are blocked. */
  private saveWebFcmToken(): void {
    if (!localStorage.getItem('auth-token')) {
      return;
    }
    void this.commonService.requestPermission().then((token) => {
      if (!token) {
        return;
      }
      this.loginService.updateFCMToken(token).subscribe({
        error: (err) => console.error('Failed to save web notification token', err),
      });
    });
  }

  private navigateAfterLogin(route: string): void {
    localStorage.removeItem('redirectUrl');
    localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
    if (route === POST_LOGIN_ROUTE || route.endsWith(POST_LOGIN_ROUTE)) {
      sessionStorage.setItem('postLoginRoute', POST_LOGIN_ROUTE);
    } else {
      sessionStorage.removeItem('postLoginRoute');
    }

    // Always navigate on the current host. DB may store absolute localhost
    // URLs or relative paths like `/admin/landing-page` — strip any host.
    const path = normalizeAppPath(route, POST_LOGIN_ROUTE);
    void this.router.navigateByUrl(path);
  }

  public generateOtp() {
    // Mark all fields as touched to trigger validation messages

    if (
      this.isPasswordField &&
      this.loginForm.value.email != undefined &&
      this.loginForm.value.password != undefined
    ) {
      this.loginForm.value.role = 'USER';
      this.loginService.userLogin(this.loginForm.value).subscribe(
        async (res: any) => {
          this.toastr.success('Login Successful');
          this.saveWebFcmToken();
          // this.storage.savedata('role', res.user.role);
          this.storage.savedata(
            'username',
            res.data.firstName + ' ' + res.data.lastName
          );
          // this.loginService.setToken(res.tokens.accessToken);
          // this.loginService.isAuthenticated = true;
          // this.loginService.setCurrentUser(res.customizedUser);

          // Store360 admin password login defaults to landing page
          this.navigateAfterLogin(POST_LOGIN_ROUTE);
        },
        (err) => {
          this.toastr.error('Login Unsuccessful');
        }
      );
    } else {
      if (
        this.isEnableOtp &&
        this.loginForm.value.email != undefined &&
        this.loginForm.value.otp != undefined
      ) {
        this.loginService.login(this.loginForm.getRawValue()).subscribe(
          async (res: any) => {
            this.storage.savedata(
              'username',
              res.result.user.profile.firstName +
              ' ' +
              res.result.user.profile.lastName
            );
            this.storage.savedata('IsProjectManagement', res.result.user.hasProjectManagementAccess);
            this.storage.savedata('profile', res.result.user.profile);
            this.storage.savedata('user', res.result.user);
            this.storage.savedata('id', res.result.user.id);
            this.storage.saveToken(res.result.tokens.accessToken);
            this.saveWebFcmToken();
            if (res.result.user) {
              localStorage.setItem('user', JSON.stringify(res.result.user));
            }

            // ── Clear stale session state from prior logins ─────────────────
            // Prevents IntElux routes (e.g. /admin/landing-page) from a
            // previous session from overriding the correct project route.
            localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
            localStorage.removeItem('redirectUrl');

            // ── Multi-project: save per-project scoped tokens ──────────────
            // Backend omits Project 1 from response.projects (global JWT is
            // IntElux). We re-add IntElux after user details confirm Store360 roles.
            const rawProjects = (res.result.projects ?? []) as ProjectTokenEntry[];
            localStorage.setItem(
              'project-tokens-p1',
              res.result.tokens.accessToken,
            );
            localStorage.setItem('accessToken', res.result.tokens.accessToken);
            localStorage.removeItem('active-project-id');
            localStorage.setItem('isProjectManager', 'false');

            if (rawProjects.length) {
              localStorage.setItem('project-tokens', JSON.stringify(rawProjects));
            } else {
              localStorage.removeItem('project-tokens');
            }

            // Fetch full user details to resolve project-specific post-login route
            this.userService.getUserDetailsById(res.result.user.id).subscribe(
              (userDetailsRes) => {
                const combinedUser = {
                  ...res.result.user,
                  ...(userDetailsRes?.result ?? {}),
                };
                localStorage.setItem('user', JSON.stringify(combinedUser));
                this.storage.savedata('user', combinedUser);
                const pmRoleId = resolveProjectManagementRoleId(
                  combinedUser.roles,
                  combinedUser.projectManagementRoleId,
                );
                this.storage.savedata('projectManagementRoleId', String(pmRoleId));
                localStorage.setItem('projectManagementRoleId', String(pmRoleId));
                localStorage.setItem('roles', JSON.stringify(combinedUser.roles ?? []));
                this.storage.savedata(
                  'IsProjectManagement',
                  combinedUser.hasProjectManagementAccess === true ? 'true' : 'false',
                );

                const projects = ensureSessionProjectTokens(rawProjects, {
                  globalToken: res.result.tokens.accessToken,
                  roles: combinedUser.roles ?? [],
                  hasPmAccess: combinedUser.hasProjectManagementAccess === true,
                  projectManagementRoleId: combinedUser.projectManagementRoleId,
                });
                if (projects.length) {
                  localStorage.setItem('project-tokens', JSON.stringify(projects));
                }

                // Sole SPFT/Shop Profiling: mark active project only.
                // Host auth-token stays the global login JWT (see resolvePostLoginRoute).
                if (
                  projects.length === 1 &&
                  !isInteluxProject(projects[0]) &&
                  !isPmProject(projects[0]) &&
                  projects[0]?.projectId != null
                ) {
                  localStorage.setItem(
                    'active-project-id',
                    String(projects[0].projectId),
                  );
                  localStorage.setItem('isProjectManager', 'false');
                }

                sessionStorage.removeItem('host-login-redirect-lock');
                const redirectUrl = this.resolvePostLoginRoute(
                  combinedUser,
                  projects,
                );
                this.navigateAfterLogin(redirectUrl);
              },
              (err) => {
                console.error('Error fetching user details:', err);
                const projects = ensureSessionProjectTokens(rawProjects, {
                  globalToken: res.result.tokens.accessToken,
                  roles: res.result.user?.roles ?? [],
                  hasPmAccess: res.result.user?.hasProjectManagementAccess === true,
                  projectManagementRoleId: res.result.user?.projectManagementRoleId,
                });
                if (projects.length) {
                  localStorage.setItem('project-tokens', JSON.stringify(projects));
                }
                const redirectUrl = this.resolvePostLoginRoute(
                  res.result.user,
                  projects,
                );
                this.navigateAfterLogin(redirectUrl);
              }
            );
          },
          (err) => {
            this.toastr.error(err.message);
          }
        );
      } else {
        this.getOpt();
      }
    }
  }

  public reSendOtp() {
    this.disableButton = true; // Disable the button
    this.startTimer(); // Start the timer
    this.getOpt();
  }

  startTimer() {
    if (!this.countdownTimer) {
      // Check if the timer is not already running
      this.timer = 60; // Set initial timer value
      this.disableButton = true; // Disable the button
      this.countdownTimer = setInterval(() => {
        this.timer--;
        if (this.timer <= 0) {
          clearInterval(this.countdownTimer); // Stop the timer when it reaches 0
          this.countdownTimer = null; // Reset the timer variable
          this.disableButton = false; // Enable the button
        }
      }, 1000); // Update the timer every second
    }
  }

  private getOpt() {
    if (this.loginForm.value.email != undefined) {
      this.loginService.generateOtp(this.loginForm.value).subscribe({
        next: (response: any) => {
          if (!environment.production) {
            this.loginForm.patchValue({ otp: response.result.otp });
          }
          this.isEnableOtp = true;
        },
        error: (err) => {
          if (err.error.message) {
            this.toastr.error(
              err.error.message + ' Please contact your admin.'
            );
          } else {
            this.toastr.error('Something went wrong. Please contact your admin.');
          }
        },
      });
    }
  }
  loginAdmin(role: string) {
    if (role === 'admin') {
      this.isPasswordField = true;
      this.loginForm.reset();
    }
  }

  // joinSurveyRoom(userId: number) {
  //   this.notificationService
  //     .sendMessage({
  //       userId
  //     });
  // }
}
